import { action, computed, observable, makeAutoObservable } from 'mobx';
import { HomePageCarouselState } from 'src/AppState/CmsState/HomePageCarouselState/HomePageCarouselState';
import { HomePageCarouselModelType } from 'src/api/config/home_page_carousel/getHomePageCarousel';
import { CmsTranslationDataModelType } from 'src/api/config/cmsTranslations/getAllCmsTranslations';
import { TrpcClient } from 'src/AppState/TrpcClient';

export class PromoCarouselItemState {
    @observable public showDeletePopup: boolean = false;
    @observable public isActive: boolean = this.homePageCarouselItem.is_active;

    public constructor(
        private homePageCarouselItem: HomePageCarouselModelType,
        private carouselPromoState: HomePageCarouselState,
        private readonly trpcClient: TrpcClient
    ) {
        makeAutoObservable(this);
    }

    @computed public get homePageCarouselItemData(): HomePageCarouselModelType {
        return this.homePageCarouselItem;
    }

    @computed public get promoId(): number {
        return this.homePageCarouselItem.id;
    }

    @computed public get promoTypeLabel(): string {
        return this.carouselPromoState.getPromoLabel(this.homePageCarouselItem.media_type);
    }

    @computed public get isEdited(): boolean {
        return this.homePageCarouselItem.id === this.carouselPromoState.editedPromoId;
    }

    @computed public get itemTranslations(): Array<CmsTranslationDataModelType> {
        return this.carouselPromoState.cmsTranslationsPromoCarouselList.filter(
            (item) => item.component_id === this.promoId
        );
    }

    @action public handleEdit = async (): Promise<void> => {
        this.carouselPromoState.resetForm();
        this.carouselPromoState.resetAllTranslationForms();
        this.carouselPromoState.setCarouselTranslations(this.itemTranslations);
        await this.carouselPromoState.setCarousel(this.homePageCarouselItem);
    };

    @action public handleDuplicate = async (): Promise<void> => {
        this.carouselPromoState.resetForm();
        this.carouselPromoState.resetAllTranslationForms();
        this.carouselPromoState.isDuplicateMode = true;
        this.carouselPromoState.setCarouselTranslations(this.itemTranslations);
        await this.carouselPromoState.setCarousel(this.homePageCarouselItem);
    };

    @action public setActive = async (): Promise<void> => {
        const updatedItemData = {
            ...this.homePageCarouselItem,
            is_active: !this.homePageCarouselItem.is_active,
        };

        this.isActive = updatedItemData.is_active;

        try {
            await this.trpcClient.client.homePageCarousel.putUpdateHomePageCarousel.mutate({
                component_id: this.homePageCarouselItem.id,
                data: updatedItemData,
            });
            this.homePageCarouselItem = updatedItemData;
            await this.carouselPromoState.promoCarouselListResource.refresh();
        } catch (error) {
            console.error(error);
        }
    };

    @action public handleDeleteCarousel = async (): Promise<void> => {
        const removeResult = await this.trpcClient.client.homePageCarousel.deleteHomePageCarousel.mutate({
            id: this.homePageCarouselItem.id,
        });

        if (removeResult.body === undefined) {
            console.log('Error deleting carousel');
        } else {
            for (const translation of this.itemTranslations) {
                await this.carouselPromoState.cmsTranslationsState.handleDeleteApiCall(translation.id);
            }
            await this.carouselPromoState.removeDisplayOrder();
            await this.carouselPromoState.cmsTranslationsState.translationsResource.refresh();
            await this.carouselPromoState.promoCarouselListResource.refresh();
        }
    };

    @action public handleShowDeletePopup = (): void => {
        this.showDeletePopup = true;
    };

    @action public handleHideDeletePopup = (): void => {
        this.showDeletePopup = false;
    };
}
